import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Col, Row } from 'reactstrap';
import { Translate, ValidatedField, ValidatedForm, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities as getAddresses } from 'app/entities/address/address.reducer';
import { getEntities as getEans } from 'app/entities/ean/ean.reducer';
import { ProductType } from 'app/shared/model/enumerations/product-type.model';
import { MeteringPeriod } from 'app/shared/model/enumerations/metering-period.model';
import { FeedType } from 'app/shared/model/enumerations/feed-type.model';
import { createEntity, getEntity, reset, updateEntity } from './meter.reducer';

export const MeterUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const addresses = useAppSelector(state => state.address.entities);
  const eans = useAppSelector(state => state.ean.entities);
  const meterEntity = useAppSelector(state => state.meter.entity);
  const loading = useAppSelector(state => state.meter.loading);
  const updating = useAppSelector(state => state.meter.updating);
  const updateSuccess = useAppSelector(state => state.meter.updateSuccess);
  const productTypeValues = Object.keys(ProductType);
  const meteringPeriodValues = Object.keys(MeteringPeriod);
  const feedTypeValues = Object.keys(FeedType);

  const handleClose = () => {
    navigate('/meter');
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getAddresses({}));
    dispatch(getEans({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    if (values.id !== undefined && typeof values.id !== 'number') {
      values.id = Number(values.id);
    }
    if (values.standaardJaarAfname !== undefined && typeof values.standaardJaarAfname !== 'number') {
      values.standaardJaarAfname = Number(values.standaardJaarAfname);
    }
    if (values.standaardJaarInvoeding !== undefined && typeof values.standaardJaarInvoeding !== 'number') {
      values.standaardJaarInvoeding = Number(values.standaardJaarInvoeding);
    }
    if (values.numberOfCounters !== undefined && typeof values.numberOfCounters !== 'number') {
      values.numberOfCounters = Number(values.numberOfCounters);
    }
    if (values.counterIndication !== undefined && typeof values.counterIndication !== 'number') {
      values.counterIndication = Number(values.counterIndication);
    }
    if (values.multiplicationFactor !== undefined && typeof values.multiplicationFactor !== 'number') {
      values.multiplicationFactor = Number(values.multiplicationFactor);
    }

    const entity = {
      ...meterEntity,
      ...values,
      meterAddress: addresses.find(it => it.id.toString() === values.meterAddress?.toString()),
      ean: eans.find(it => it.id.toString() === values.ean?.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {}
      : {
          productType: 'ELECTRICITY',
          meteringPeriod: 'INT',
          feedType: 'IN',
          ...meterEntity,
          meterAddress: meterEntity?.meterAddress?.id,
          ean: meterEntity?.ean?.id,
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="crawlerBackendApp.meter.home.createOrEditLabel" data-cy="MeterCreateUpdateHeading">
            <Translate contentKey="crawlerBackendApp.meter.home.createOrEditLabel">Create or edit a Meter</Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="meter-id"
                  label={translate('global.field.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('crawlerBackendApp.meter.balanceSupplierCompanyId')}
                id="meter-balanceSupplierCompanyId"
                name="balanceSupplierCompanyId"
                data-cy="balanceSupplierCompanyId"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.meter.balanceResponsibleCompanyId')}
                id="meter-balanceResponsibleCompanyId"
                name="balanceResponsibleCompanyId"
                data-cy="balanceResponsibleCompanyId"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.meter.meteringResponsibleCompanyId')}
                id="meter-meteringResponsibleCompanyId"
                name="meteringResponsibleCompanyId"
                data-cy="meteringResponsibleCompanyId"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.meter.technicalCommunicationSM')}
                id="meter-technicalCommunicationSM"
                name="technicalCommunicationSM"
                data-cy="technicalCommunicationSM"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.meter.allocationMethod')}
                id="meter-allocationMethod"
                name="allocationMethod"
                data-cy="allocationMethod"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.meter.contractedCapacity')}
                id="meter-contractedCapacity"
                name="contractedCapacity"
                data-cy="contractedCapacity"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.meter.energyDeliveryStatus')}
                id="meter-energyDeliveryStatus"
                name="energyDeliveryStatus"
                data-cy="energyDeliveryStatus"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.meter.invoiceMonth')}
                id="meter-invoiceMonth"
                name="invoiceMonth"
                data-cy="invoiceMonth"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.meter.maxConsumption')}
                id="meter-maxConsumption"
                name="maxConsumption"
                data-cy="maxConsumption"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.meter.physicalCapacity')}
                id="meter-physicalCapacity"
                name="physicalCapacity"
                data-cy="physicalCapacity"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.meter.isSmartMeterTurnedOn')}
                id="meter-isSmartMeterTurnedOn"
                name="isSmartMeterTurnedOn"
                data-cy="isSmartMeterTurnedOn"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.meter.standaardJaarAfname')}
                id="meter-standaardJaarAfname"
                name="standaardJaarAfname"
                data-cy="standaardJaarAfname"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.meter.standaardJaarInvoeding')}
                id="meter-standaardJaarInvoeding"
                name="standaardJaarInvoeding"
                data-cy="standaardJaarInvoeding"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.meter.isSpecialMeteringPoint')}
                id="meter-isSpecialMeteringPoint"
                name="isSpecialMeteringPoint"
                data-cy="isSpecialMeteringPoint"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.meter.isDoubleMeter')}
                id="meter-isDoubleMeter"
                name="isDoubleMeter"
                data-cy="isDoubleMeter"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.meter.transactionDossierNumber')}
                id="meter-transactionDossierNumber"
                name="transactionDossierNumber"
                data-cy="transactionDossierNumber"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.meter.reference')}
                id="meter-reference"
                name="reference"
                data-cy="reference"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.meter.startDate')}
                id="meter-startDate"
                name="startDate"
                data-cy="startDate"
                type="date"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.meter.endDate')}
                id="meter-endDate"
                name="endDate"
                data-cy="endDate"
                type="date"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.meter.productType')}
                id="meter-productType"
                name="productType"
                data-cy="productType"
                type="select"
              >
                {productTypeValues.map(productType => (
                  <option value={productType} key={productType}>
                    {translate(`crawlerBackendApp.ProductType.${productType}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('crawlerBackendApp.meter.meteringPeriod')}
                id="meter-meteringPeriod"
                name="meteringPeriod"
                data-cy="meteringPeriod"
                type="select"
              >
                {meteringPeriodValues.map(meteringPeriod => (
                  <option value={meteringPeriod} key={meteringPeriod}>
                    {translate(`crawlerBackendApp.MeteringPeriod.${meteringPeriod}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('crawlerBackendApp.meter.feedType')}
                id="meter-feedType"
                name="feedType"
                data-cy="feedType"
                type="select"
              >
                {feedTypeValues.map(feedType => (
                  <option value={feedType} key={feedType}>
                    {translate(`crawlerBackendApp.FeedType.${feedType}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('crawlerBackendApp.meter.profileCategory')}
                id="meter-profileCategory"
                name="profileCategory"
                data-cy="profileCategory"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.meter.capacityTariffCode')}
                id="meter-capacityTariffCode"
                name="capacityTariffCode"
                data-cy="capacityTariffCode"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.meter.physicalStatus')}
                id="meter-physicalStatus"
                name="physicalStatus"
                data-cy="physicalStatus"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.meter.usageSegment')}
                id="meter-usageSegment"
                name="usageSegment"
                data-cy="usageSegment"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.meter.locationDescription')}
                id="meter-locationDescription"
                name="locationDescription"
                data-cy="locationDescription"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.meter.meterNumber')}
                id="meter-meterNumber"
                name="meterNumber"
                data-cy="meterNumber"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.meter.meterType')}
                id="meter-meterType"
                name="meterType"
                data-cy="meterType"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.meter.administrativeStatus')}
                id="meter-administrativeStatus"
                name="administrativeStatus"
                data-cy="administrativeStatus"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.meter.smartMeterReadability')}
                id="meter-smartMeterReadability"
                name="smartMeterReadability"
                data-cy="smartMeterReadability"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.meter.temperatureCorrection')}
                id="meter-temperatureCorrection"
                name="temperatureCorrection"
                data-cy="temperatureCorrection"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.meter.numberOfCounters')}
                id="meter-numberOfCounters"
                name="numberOfCounters"
                data-cy="numberOfCounters"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.meter.counterIndication')}
                id="meter-counterIndication"
                name="counterIndication"
                data-cy="counterIndication"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.meter.tariffZone')}
                id="meter-tariffZone"
                name="tariffZone"
                data-cy="tariffZone"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.meter.meteringDirection')}
                id="meter-meteringDirection"
                name="meteringDirection"
                data-cy="meteringDirection"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.meter.countingWheels')}
                id="meter-countingWheels"
                name="countingWheels"
                data-cy="countingWheels"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.meter.multiplicationFactor')}
                id="meter-multiplicationFactor"
                name="multiplicationFactor"
                data-cy="multiplicationFactor"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.meter.initialStartDate')}
                id="meter-initialStartDate"
                name="initialStartDate"
                data-cy="initialStartDate"
                type="date"
              />
              <ValidatedField
                id="meter-meterAddress"
                name="meterAddress"
                data-cy="meterAddress"
                label={translate('crawlerBackendApp.meter.meterAddress')}
                type="select"
              >
                <option value="" key="0" />
                {addresses
                  ? addresses.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="meter-ean"
                name="ean"
                data-cy="ean"
                label={translate('crawlerBackendApp.meter.ean')}
                type="select"
              >
                <option value="" key="0" />
                {eans
                  ? eans.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <Button tag={Link} id="cancel-save" data-cy="entityCreateCancelButton" to="/meter" replace color="info">
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button
                color="primary"
                id="save-entity"
                data-cy="entityCreateSaveButton"
                type="submit"
                disabled={updating}
              >
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default MeterUpdate;
